import { AlertType, Season, TimeSlot } from '@types';
import {
  AlertModal,
  Card,
  EmptyState,
  LoadingSpinner,
  SectionHeader,
} from '_atoms';
import { SeasonDetailsService, TicketService } from '_services';
import HelperMethods from '_utils/HelperMethods';
import React, { useCallback, useEffect, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { MdEvent, MdPeople, MdSchedule } from 'react-icons/md';
import { useLocation } from 'react-router-dom';

interface SelectSessionDateTimeProps {
  selectedTimeSlot?: TimeSlot;
  onNext: (data?: any) => void;
  onStepComplete: (data: any) => void;
}

export const SelectSessionDateTime: React.FC<SelectSessionDateTimeProps> = ({
  selectedTimeSlot,
  onNext,
  onStepComplete,
}) => {
  const location = useLocation();
  // Known routes that should not be treated as link codes
  const knownRoutes = [
    'tickets',
    'passes',
    'parties',
    'lessons',
    'hockey',
    'waivers',
    'contact-us',
    'sign-in',
    'sign-up',
    'forgot-password',
    'my-account',
    'admin',
  ];

  const pathSegments = location.pathname.split('/').filter(Boolean);
  const firstSegment = pathSegments[0] || '';
  const linkOnlyUrlCode =
    firstSegment && !knownRoutes.includes(firstSegment.toLowerCase())
      ? firstSegment
      : null;

  // Parse date from link code if available (e.g., "oct31BATS" -> "2025-10-31")
  const parsedDateFromLinkCode = linkOnlyUrlCode
    ? HelperMethods.parseDateFromLinkCode(linkOnlyUrlCode)
    : null;

  // Convert parsed date to local YYYY-MM-DD format for selectedDate
  const getLocalDateString = (date: Date): string => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // Parse YYYY-MM-DD string as local date (not UTC)
  const parseLocalDate = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const initialDate = parsedDateFromLinkCode
    ? parsedDateFromLinkCode // Already in YYYY-MM-DD format
    : selectedTimeSlot?.date || getLocalDateString(new Date());

  console.log('initialDate', initialDate);
  console.log('parsedDateFromLinkCode', parsedDateFromLinkCode);

  const [selectedDate, setSelectedDate] = useState<string>(initialDate);
  const [currentSelectedTimeSlot, setCurrentSelectedTimeSlot] = useState<
    TimeSlot | undefined
  >(selectedTimeSlot);
  const [settings, setSettings] = useState<Season>({
    id: '',
    startDate: '',
    endDate: '',
    isTicketPurchasesEnabled: false,
    isSeasonPassPurchasesEnabled: false,
    isPartyPackagePurchasesEnabled: false,
    isSkatingLessonPurchasesEnabled: false,
    isHockeyLessonPurchasesEnabled: false,
    sessionTimes: [],
  });
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: AlertType;
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });
  // Load current season details on page load
  useEffect(() => {
    const loadCurrentSeason = async () => {
      setIsLoadingInitial(true);
      try {
        const responseData =
          (await SeasonDetailsService.getCurrentSeasonDetails()) as unknown as Season;

        if (
          responseData &&
          (responseData.startDate ||
            responseData.endDate ||
            responseData.sessionTimes.length > 0)
        ) {
          setSettings(responseData);
        } else {
          showAlert(
            'info',
            'Info',
            'There are currently no active season settings.',
          );
        }
      } catch (error) {
        console.error('Error loading current season:', error);
        showAlert('error', 'Error', 'Failed to load current season settings.');
      } finally {
        setIsLoadingInitial(false);
      }
    };

    loadCurrentSeason();
  }, []);

  // Update selectedDate when link code changes and contains a date
  useEffect(() => {
    if (parsedDateFromLinkCode) {
      // parsedDateFromLinkCode is already in YYYY-MM-DD format (local timezone)
      setSelectedDate(parsedDateFromLinkCode);
    }
  }, [linkOnlyUrlCode, parsedDateFromLinkCode]);

  const loadTimeSlotsForDate = useCallback(
    async (date: string) => {
      setIsLoadingTimeSlots(true);
      try {
        // Convert ISO date string to YYYY-MM-DD format for API
        // If the date is already in YYYY-MM-DD format from link code parsing, use it as-is
        const dateStr = date.includes('T')
          ? date.split('T')[0]
          : date.split(' ')[0];

        console.log('dateStr', dateStr);
        const responseData = (await TicketService.getTimeSlotsForDate(
          dateStr,
        )) as unknown as TimeSlot[];

        // Filter out past timeslots for today
        const todayLocal = getLocalDateString(new Date());
        const currentTime = new Date();

        // If there's a linkOnlyUrlCode in the URL, show only matching link-only timeslots
        // Otherwise, filter out all link-only timeslots
        let filteredTimeSlots = responseData || [];

        if (linkOnlyUrlCode) {
          // Show only timeslots where isLinkOnly is true and LinkOnlyUrlCode matches
          // Decode the linkOnlyUrlCode from the API response if it's encoded
          filteredTimeSlots = filteredTimeSlots.filter(timeSlot => {
            if (!timeSlot.isLinkOnly) return false;

            // Decode the encoded link code from the API
            const decodedApiCode = HelperMethods.decodeLinkOnlyUrlCode(
              timeSlot.linkOnlyUrlCode,
            );

            // Compare decoded code with the URL code (case-insensitive)
            return (
              decodedApiCode?.toLowerCase() === linkOnlyUrlCode.toLowerCase()
            );
          });
        } else {
          // Filter out link-only timeslots for normal bookings
          filteredTimeSlots = filteredTimeSlots.filter(
            timeSlot => !timeSlot.isLinkOnly,
          );
        }

        // If the selected date is today, filter past sessions (beyond 2 hours ago) and mark timeslots within 30 minutes as disabled
        if (todayLocal === dateStr) {
          // Calculate cutoff time: 2 hours ago
          const twoHoursAgo = new Date(
            currentTime.getTime() - 2 * 60 * 60 * 1000,
          );

          filteredTimeSlots = filteredTimeSlots
            .filter(timeSlot => {
              // Parse the timeslot time (assuming format like "14:30" or "2:30 PM")
              const timeSlotTime = timeSlot.timeSlot;
              const [time, period] = timeSlotTime.split(' ');
              const timeslotDateTime = parseLocalDate(dateStr);

              let slotHour: number;
              let slotMinutes: number;

              if (period) {
                // Handle AM/PM format
                const [hours, minutes] = time.split(':').map(Number);
                slotHour = hours;
                slotMinutes = minutes || 0;

                if (period === 'PM' && hours !== 12) {
                  slotHour = hours + 12;
                } else if (period === 'AM' && hours === 12) {
                  slotHour = 0;
                }
              } else {
                // Handle 24-hour format
                const [hours, minutes] = time.split(':').map(Number);
                slotHour = hours;
                slotMinutes = minutes || 0;
              }

              timeslotDateTime.setHours(slotHour, slotMinutes, 0, 0);

              // Show sessions from 2 hours ago onwards
              return timeslotDateTime >= twoHoursAgo;
            })
            .map(timeSlot => {
              // Parse the timeslot time again for the disabled check
              const timeSlotTime = timeSlot.timeSlot;
              const timeslotDateTime = parseLocalDate(dateStr);
              const [time, period] = timeSlotTime.split(' ');

              if (period) {
                // Handle AM/PM format
                const [hours, minutes] = time.split(':').map(Number);
                let hour24 = hours;

                if (period === 'PM' && hours !== 12) {
                  hour24 = hours + 12;
                } else if (period === 'AM' && hours === 12) {
                  hour24 = 0;
                }

                timeslotDateTime.setHours(hour24, minutes || 0, 0, 0);
              } else {
                // Handle 24-hour format
                const [hours, minutes] = time.split(':').map(Number);
                timeslotDateTime.setHours(hours, minutes || 0, 0, 0);
              }

              // Check if timeslot is within 30 minutes (only for future timeslots and only for today)
              const thirtyMinutesFromNow = new Date(
                currentTime.getTime() + 30 * 60 * 1000,
              );

              // Only apply the 30-minute check if the timeslot date is the same as today's date
              const timeslotDateOnly = new Date(
                timeslotDateTime.getFullYear(),
                timeslotDateTime.getMonth(),
                timeslotDateTime.getDate(),
              );
              const currentDateOnly = new Date(
                currentTime.getFullYear(),
                currentTime.getMonth(),
                currentTime.getDate(),
              );

              const isWithin30Minutes =
                timeslotDateOnly.getTime() === currentDateOnly.getTime() &&
                timeslotDateTime <= thirtyMinutesFromNow;

              return {
                ...timeSlot,
                isDisabled: isWithin30Minutes || timeSlot.maxCapacity === 0,
              };
            });
        }

        setTimeSlots(filteredTimeSlots);
      } catch (error) {
        console.error('Error loading time slots:', error);
        showAlert(
          'error',
          'Error',
          'Failed to load skating sessions for the selected date.',
        );
        setTimeSlots([]);
      } finally {
        setIsLoadingTimeSlots(false);
      }
    },
    [linkOnlyUrlCode],
  );

  useEffect(() => {
    if (selectedDate) {
      console.log('selectedDate', selectedDate);
      loadTimeSlotsForDate(selectedDate);
    } else {
      setTimeSlots([]);
    }
  }, [selectedDate, loadTimeSlotsForDate]);

  const showAlert = (type: AlertType, title: string, message: string) => {
    setAlertModal({ isOpen: true, type, title, message });
  };

  const closeAlert = () => {
    setAlertModal({ isOpen: false, type: 'info', title: '', message: '' });
  };

  const handleContinue = () => {
    if (selectedDate && currentSelectedTimeSlot) {
      onStepComplete({ selectedTimeSlot: currentSelectedTimeSlot });
      onNext({ selectedTimeSlot: currentSelectedTimeSlot });
    }
  };

  const NotInSeason = () => (
    <div className="flex items-center justify-center min-h-[400px]">
      <Card className="w-full max-w-md justify-center items-center border-none">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
            <MdEvent className="w-8 h-8 text-gray-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              We are currently not in Season
            </h2>
            <p className="text-gray-600">
              Please check back in the future to book a session.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {!settings.isTicketPurchasesEnabled && !isLoadingInitial ? (
        <NotInSeason />
      ) : (
        <>
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold text-gray-900">
              Select Session Date & Time
            </h2>
            <p className="text-gray-600 text-lg">
              Choose when you'd like to go skating
            </p>
          </div>

          {isLoadingInitial ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex flex-col lg:flex-row gap-6">
                {/* Date Selection */}
                <div className="lg:w-1/2">
                  {settings && settings.startDate && (
                    <Card className="w-full">
                      <div className="p-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                          <MdEvent className="mr-2 text-blue-600" />
                          Select Date
                        </h3>
                        <DayPicker
                          mode="single"
                          startMonth={
                            settings.startDate
                              ? new Date(settings.startDate)
                              : new Date()
                          }
                          selected={
                            selectedDate
                              ? parseLocalDate(selectedDate)
                              : undefined
                          }
                          onSelect={e =>
                            setSelectedDate(e ? getLocalDateString(e) : '')
                          }
                          modifiers={{
                            disabled: [
                              // Disable past dates (before today)
                              {
                                before: new Date(
                                  new Date().setHours(0, 0, 0, 0),
                                ),
                              },
                              // Disable dates outside season if season dates are set
                              ...(settings.startDate && settings.endDate
                                ? [
                                    { before: new Date(settings.startDate) },
                                    { after: new Date(settings.endDate) },
                                  ]
                                : []),
                            ],
                          }}
                          footer={
                            <div className="text-sm text-gray-500 mt-4 p-3 bg-gray-50 rounded-lg">
                              {settings.startDate && settings.endDate && (
                                <div className="text-center">
                                  <span className="font-medium">
                                    Season runs from
                                  </span>
                                  <br />
                                  {new Date(
                                    settings.startDate,
                                  ).toLocaleDateString()}{' '}
                                  to{' '}
                                  {new Date(
                                    settings.endDate,
                                  ).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          }
                        />
                      </div>
                    </Card>
                  )}
                </div>

                {/* Time Slots */}
                <div className="lg:w-1/2">
                  {!selectedDate ? (
                    <Card className="w-full h-full flex items-center justify-center">
                      <EmptyState
                        title="No Date Selected"
                        description="Please select a date to view skating sessions."
                        icon={MdEvent}
                      />
                    </Card>
                  ) : (
                    <div className="w-full p-2">
                      <SectionHeader
                        title={`Sessions for ${(() => {
                          // Extract YYYY-MM-DD from ISO string to avoid timezone issues
                          const dateOnly = selectedDate.includes('T')
                            ? selectedDate.split('T')[0]
                            : selectedDate.split(' ')[0];
                          const [year, month, day] = dateOnly.split('-');
                          return `${month}/${day}/${year}`;
                        })()}`}
                        icon={MdSchedule}
                      />

                      {isLoadingTimeSlots ? (
                        <div className="flex items-center justify-center py-8">
                          <LoadingSpinner />
                        </div>
                      ) : timeSlots.length === 0 ? (
                        <EmptyState
                          title="No Skating Sessions Found"
                          description={`No skating sessions found for ${parseLocalDate(selectedDate).toLocaleDateString()}.`}
                          icon={MdSchedule}
                        />
                      ) : (
                        <div className="space-y-3 mt-4 w-full">
                          {timeSlots.map((timeSlot: any) => (
                            <div
                              key={timeSlot.id}
                              onClick={() =>
                                !timeSlot.isDisabled &&
                                setCurrentSelectedTimeSlot(timeSlot)
                              }
                              className={
                                timeSlot.isDisabled
                                  ? 'cursor-not-allowed'
                                  : 'cursor-pointer'
                              }
                            >
                              <div
                                className={`flex flex-col w-full border rounded-lg transition-all duration-200 ${
                                  timeSlot.isDisabled
                                    ? 'bg-gray-100 border-gray-300 opacity-60'
                                    : currentSelectedTimeSlot?.id ===
                                        timeSlot.id
                                      ? 'bg-blue-50 border-blue-300 shadow-md hover:shadow-md'
                                      : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-md'
                                }`}
                              >
                                <div className="flex w-full gap-2 sm:gap-4 justify-between p-3 sm:p-2">
                                  <div className="flex items-center">
                                    <div className="w-6 h-6 bg-blue-100 mr-2 rounded-full flex items-center justify-center">
                                      <MdSchedule className="w-4 h-4  text-blue-600" />
                                    </div>
                                    <div>
                                      <div className="font-medium text-gray-900 text-sm sm:text-base">
                                        {HelperMethods.formatTime12Hour(
                                          timeSlot.timeSlot,
                                        )}
                                      </div>
                                      <div className="text-xs sm:text-sm text-gray-500">
                                        Start Time
                                      </div>
                                    </div>
                                  </div>

                                  <div className="text-right">
                                    {timeSlot.isDisabled ? (
                                      <div className="text-center">
                                        <div className="text-xs text-red-600 font-medium">
                                          {timeSlot.maxCapacity > 0
                                            ? 'Tickets only available at Ticket Booth'
                                            : 'SOLD OUT'}
                                        </div>
                                        <div className="flex items-center justify-end space-x-1 sm:space-x-2">
                                          <MdPeople className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                          <span className="font-semibold text-gray-900 text-sm sm:text-base">
                                            {timeSlot.maxCapacity < 1
                                              ? 0
                                              : timeSlot.maxCapacity}
                                          </span>
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        <div className="flex items-center justify-end space-x-1 sm:space-x-2">
                                          <MdPeople className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                          <span className="font-semibold text-gray-900 text-sm sm:text-base">
                                            {timeSlot.maxCapacity < 1
                                              ? 0
                                              : timeSlot.maxCapacity}
                                          </span>
                                        </div>
                                        <div className="text-xs sm:text-sm text-gray-500">
                                          Available
                                        </div>
                                        {timeSlot.maxCapacity < 1 ? (
                                          <div className="text-xs text-red-600 font-bold mt-1">
                                            SOLD OUT
                                          </div>
                                        ) : (
                                          timeSlot.maxCapacity < 20 && (
                                            <div className="text-xs text-red-600 font-medium mt-1">
                                              Hurry up! Almost sold out!
                                            </div>
                                          )
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>

                                {/* Pricing Information - At the bottom */}
                                {!timeSlot.isDisabled &&
                                  (timeSlot.adultPrice !== 999 ||
                                    timeSlot.childPrice !== 999) && (
                                    <div className="px-3 border-rounded-b-lg pb-3 sm:px-2 sm:pb-2 pt-2 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                                      <div className="flex justify-center space-x-4 text-sm ">
                                        {timeSlot.adultPrice !== 999 && (
                                          <div className="text-gray-700">
                                            Adult: $
                                            {timeSlot.adultPrice.toFixed(0)}
                                          </div>
                                        )}
                                        {timeSlot.childPrice !== 999 && (
                                          <div className="text-gray-700">
                                            Child: $
                                            {timeSlot.childPrice.toFixed(0)}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Continue Button */}
              <div className="flex justify-center sm:justify-end pt-6">
                <button
                  onClick={handleContinue}
                  disabled={
                    !currentSelectedTimeSlot ||
                    currentSelectedTimeSlot.maxCapacity < 1
                  }
                  className="w-full sm:w-auto px-6 sm:px-8 py-4 sm:py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md text-base sm:text-sm"
                >
                  Continue
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />
    </div>
  );
};
